import { Component, EventEmitter, Input, Output } from "@angular/core";
import { ConditionSet, Control, DynamicForm, FormConditions } from "../../models/dynamic-form";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subscription } from "rxjs";
import { NgClass, NgStyle } from "@angular/common";
import { DynamicInputComponent } from "./dynamic-input/dynamic-input.component";
import { DynamicFormService } from "../../services/dynamic-form.service";
import { PermissionService } from "../../services/permission.service";
import { LoadingService } from "../../services/loading.service";

@Component({
  selector: "app-dynamic-form",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgStyle,
    NgClass,
    DynamicInputComponent
  ],
  templateUrl: "./dynamic-form.component.html",
  styleUrl: "./dynamic-form.component.scss"
})
export class DynamicFormComponent {
  @Input() formConfig: DynamicForm | undefined;
  @Input() formGroup!: FormGroup;
  @Input() readOnly: boolean = false;

  dependentFieldMap: Map<string, Set<string>> = new Map();

  @Output() onValueChange = new EventEmitter<{ controlName: string, result: any }>();
  @Output() searchTextCleared = new EventEmitter<any>();


  requiredImplyingValidators = new Set([
    "required",
    "selectRequired",
    "autocompleteRequired"
  ]);

  handleValueChange(event: { controlName: string, result: any }) {
    this.onValueChange.emit(event);
  }

  onAutoCompleteSearchTermCleared(controlName: string) {
    this.searchTextCleared.emit(controlName);
  }

  private controlMetadataMap: Map<string, Control> = new Map();

  constructor(private dynamicFormService: DynamicFormService,
              private permissionService: PermissionService) {
  }

  ngOnInit() {
    this.listenToConditionChangesV2(this.formConfig);
  }


  isControlRequired(control: Control) {
    if (!control.validators || control.validators.length === 0 || this.formGroup.get(control.label)?.disabled) return false;

    return control.validators.some(validator => {
      if (!this.requiredImplyingValidators.has(validator.type)) {
        return false;
      }

      if (!validator.conditions) {
        return true;
      }

      return this.dynamicFormService.evaluateConditionSet(validator.conditions, this.formGroup, true);
    });
  }

  listenToConditionChanges(config: DynamicForm | undefined): void {
    if (!config) return;
    const dependentFieldMap: Map<string, Set<string>> = new Map();

    config.formRows.forEach(row => {
      row.formGroups.forEach(group => {
        const control = group.control;
        if (group.control?.label) {
          this.controlMetadataMap.set(group.control.label, group.control);
        }
        if (control.conditions) {
          ["visibility", "disabled"].forEach(conditionType => {
            const condition = control.conditions?.[conditionType as keyof FormConditions];
            if (condition) {
              condition.rules.forEach(rule => {
                const dependentField = rule.field;
                if (dependentField && !dependentFieldMap.has(dependentField)) {
                  dependentFieldMap.set(dependentField, new Set());
                }
                dependentFieldMap.get(dependentField)?.add(control.label);
              });
            }
          });
        }
      });
    });

    // Subscribe to dependentField changes
    dependentFieldMap.forEach((targetFields, dependentField) => {
      const fieldControl = this.formGroup.get(dependentField);

      if (fieldControl) {
        fieldControl.valueChanges.subscribe(() => {
          targetFields.forEach(targetField => {
            const targetControl = this.formGroup.get(targetField);
            const fieldControl = this.controlMetadataMap.get(targetField);

            if (fieldControl && targetControl) {

              const isVisible = this.dynamicFormService.evaluateConditionSet(fieldControl?.conditions?.visibility, this.formGroup, true);
              const isDisabled = (fieldControl.disabled ?? false) || (this.dynamicFormService.evaluateConditionSet(fieldControl?.conditions?.visibility, this.formGroup, false));

              if (!isVisible) {
                fieldControl.hidden = true;
                targetControl.clearValidators();
                targetControl.disable();
              } else {
                fieldControl.hidden = false;
                const validators = this.dynamicFormService.getValidators(fieldControl.validators || [], this.formGroup);
                targetControl.setValidators(validators);
                isDisabled ? targetControl.disable() : targetControl.enable();
              }
              targetControl.updateValueAndValidity();
            }
          });
        });
      }
    });
  }

  listenToConditionChangesV2(config: DynamicForm | undefined): void {
    if (!config) return;

    // 1. Build dependency map with memoization
    this.buildDependencyGraph(config);

    // 2. Set up optimized subscriptions
    this.setupConditionSubscriptions();
  }


  private buildDependencyGraph(config: DynamicForm) {

    config.formRows.forEach(row => {
      row.formGroups.forEach(group => {
        const control = group.control;
        if (!control?.label) return;

        // 1. Track control metadata
        this.controlMetadataMap.set(control.label, control);

        // 2. Process visibility/disabled conditions
        ["visibility", "disabled"].forEach(conditionType => {
          const condition = control.conditions?.[conditionType as keyof FormConditions];
          this.addConditionDependencies(condition, control.label, this.dependentFieldMap);
        });

        // 3. Process validator conditions (NEW)
        control.validators?.forEach(validator => {
          if (validator.conditions) {
            this.addConditionDependencies(validator.conditions, control.label, this.dependentFieldMap);
          }
        });
      });
    });
  }

  private addConditionDependencies(conditionSet: ConditionSet | undefined, targetField: string, dependencyMap: Map<string, Set<string>>): void {
    conditionSet?.rules.forEach(rule => {
      if (!rule.field) return;

      if (!dependencyMap.has(rule.field)) {
        dependencyMap.set(rule.field, new Set());
      }
      dependencyMap.get(rule.field)?.add(targetField);
    });
  }

  protected isControlVisible(control: Control) {
    if (control.hidden ?? false) return false;
    const fieldVisible = this.dynamicFormService.evaluateConditionSet(control?.conditions?.visibility, this.formGroup, true);
    const permVisible = this.dynamicFormService.evaluatePermissionConditionSet(control?.permissionConditions?.visibility, true);
    return fieldVisible && permVisible;
  }

  protected isVisible(control: Control) {
    const fieldVisible = this.dynamicFormService.evaluateConditionSet(control?.conditions?.visibility, this.formGroup, true);
    const permVisible = this.dynamicFormService.evaluatePermissionConditionSet(control?.permissionConditions?.visibility, true);
    return fieldVisible && permVisible;
  }


  protected isControlReadOnly(control: Control) {
    if (control.isReadOnly ?? false) return true;
    const fieldReadOnly = this.dynamicFormService.evaluateConditionSet(control?.conditions?.readOnly, this.formGroup, false);
    const permReadOnly = this.dynamicFormService.evaluatePermissionConditionSet(control?.permissionConditions?.readOnly, false);
    return fieldReadOnly || permReadOnly;
  }

  protected isControlDisabled(controlConfig: Control) {
    return controlConfig?.disabled ||
      this.dynamicFormService.evaluateConditionSet(controlConfig.conditions?.disabled, this.formGroup, false) ||
      this.dynamicFormService.evaluatePermissionConditionSet(controlConfig.permissionConditions?.disabled, false);
  }

  private setupConditionSubscriptions(): void {
    const subscriptionCache = new Map<string, Subscription>();

    this.dependentFieldMap.forEach((targetFields, dependentField) => {
      const fieldControl = this.formGroup.get(dependentField);
      if (!fieldControl) return;

      // Clean up previous subscription if exists
      subscriptionCache.get(dependentField)?.unsubscribe();

      // Create new optimized subscription
      const sub = fieldControl.valueChanges.pipe(
        debounceTime(50),
        distinctUntilChanged()
      ).subscribe(() => {
        this.updateTargetFields(targetFields);
      });

      subscriptionCache.set(dependentField, sub);
    });
  }

  private updateTargetFields(targetFields: Set<string>): void {
    targetFields.forEach(targetField => {
      const targetControl = this.formGroup.get(targetField);
      const controlConfig = this.controlMetadataMap.get(targetField);

      if (!targetControl || !controlConfig) return;

      // Evaluate conditions in single pass
      const { isVisible, isDisabled } = this.evaluateFieldConditions(controlConfig);

      if (!isVisible) {
        controlConfig.hidden = true;
        targetControl.clearValidators();
        targetControl.disable();
      } else {
        controlConfig.hidden = false;
        // Validators now include conditional requirements
        targetControl.setValidators(
          this.dynamicFormService.getValidators(
            controlConfig.validators || [],
            this.formGroup
          )
        );
        isDisabled || this.readOnly ? targetControl.disable() : targetControl.enable();
      }

      targetControl.updateValueAndValidity({ onlySelf: true });
    });
  }

  private evaluateFieldConditions(controlConfig: Control): {
    isVisible: boolean;
    isDisabled: boolean
  } {
    return {
      isVisible: this.isVisible(controlConfig),
      isDisabled: this.isControlDisabled(controlConfig),
    };
  }
}
